import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Slider,
  Drawer,
  CircularProgress,
  CardMedia,
  Grow,
  Fab,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  Category as CategoryIcon,
  Public as PublicIcon,
  Today as TodayIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  TravelExplore as TravelExploreIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ContentCopy as ContentCopyIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import api, { 
  getTravelPlans, 
  deleteTravelPlan, 
  createTravelPlan, 
  searchTravelPlans, 
  likeTravelPlan,
  unlikeTravelPlan,
  copyTravelPlan,
  getLikedTravelPlans,
  updateTravelPlan,
  updateTravelPlanCoverImage,
  uploadTravelPlanCoverImage
} from '../services/api';
import { format, differenceInDays, addDays, isBefore, startOfDay } from 'date-fns';
import TravelPlanForm from '../components/TravelPlanForm';
import TravelPlanCard from '../components/TravelPlanCard';
import TripDetails from '../components/TripDetails';
import AnimatedTravelButton from '../components/AnimatedTravelButton';
import CoverImageUploadDialog from '../components/CoverImageUploadDialog';
import { useTravelPlans } from '../hooks/useTravelPlans';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet-async';

function Dashboard() {
  const getCoverImageUrl = (url) => {
    if (!url) return 'https://placehold.co/400x200/e0e0e0/909090?text=Travel+Plan';
    
    if (url.startsWith('http')) {
      return url;
    }
    
    // For paths starting with '/', treat them as public folder paths
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }
    
    return url;
  };
  
  const [activeTab, setActiveTab] = useState(0);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const { token, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const outletContext = useOutletContext() || {};
  const { handleDrawerToggle } = outletContext;
  
  // Use the custom hook for travel plans management
  const {
    travelPlans,
    filteredPlans,
    upcomingPlans: hookUpcomingPlans,
    pastPlansByYear: hookPastPlansByYear,
    availableTags,
    availableDestinations,
    loading: plansLoading,
    error: plansError,
    isDataFresh,
    searchQuery,
    sortOption,
    tagFilter,
    destinationFilter,
    handleSearchChange,
    handleSortChange,
    handleTagFilterChange,
    handleDestinationFilterChange,
    handleClearFilters: hookClearFilters,
    handleDeletePlan: hookDeletePlan,
    handleUpdatePlan: hookUpdatePlan,
    handleAddPlan,
    handleRefresh,
    handleClearError
  } = useTravelPlans();
  
  // States for Explore tab
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popularTags, setPopularTags] = useState([
    'Beach', 'Mountains', 'City', 'Adventure', 'Relaxation', 
    'Culture', 'Food', 'Shopping', 'Nature', 'History'
  ]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dateRange, setDateRange] = useState([0, 30]);
  
  // Cover image upload state
  const [coverImageDialogOpen, setCoverImageDialogOpen] = useState(false);
  const [selectedPlanForCover, setSelectedPlanForCover] = useState(null);
  
  // Cover image handlers
  const handleEditCoverImage = (planId) => {
    const plan = travelPlans.find(p => p._id === planId || p.id === planId);
    if (plan) {
      setSelectedPlanForCover(plan);
      setCoverImageDialogOpen(true);
    }
  };

  const handleCoverImageUpload = async (imageUrl, file) => {
    if (!selectedPlanForCover) return;
    
    try {
      if (imageUrl === 'FILE_UPLOAD' && file) {
        // Handle file upload to GCP Storage
        const response = await uploadTravelPlanCoverImage(selectedPlanForCover._id || selectedPlanForCover.id, file);
        await handleRefresh();
        enqueueSnackbar('Cover image uploaded successfully!', { variant: 'success' });
      } else {
        // Handle preset image URL
        await updateTravelPlanCoverImage(selectedPlanForCover._id || selectedPlanForCover.id, imageUrl);
        await handleRefresh();
        enqueueSnackbar('Cover image updated successfully!', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error updating cover image:', error);
      enqueueSnackbar('Failed to update cover image', { variant: 'error' });
    }
  };

  const handleCloseCoverImageDialog = () => {
    setCoverImageDialogOpen(false);
    setSelectedPlanForCover(null);
  };

  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [categories] = useState([
    'Beach Vacation', 'City Trip', 'Adventure', 'Family Holiday', 
    'Solo Travel', 'Couples Retreat', 'Business Trip'
  ]);
  const [popularDestinations] = useState([
    'Paris', 'New York', 'Tokyo', 'Rome', 'London', 
    'Barcelona', 'Sydney', 'Bangkok', 'Dubai', 'Bali'
  ]);

  // New state for liked plans (for Explore tab)
  const [likedPlans, setLikedPlans] = useState(new Set());

  // Form dialog states
  const [openDialog, setOpenDialog] = useState(false);
  
  // Trip details modal states
  const [tripDetailsOpen, setTripDetailsOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);

  // State for current year in past trips
  const [currentPastTripsYear, setCurrentPastTripsYear] = useState(null);

  // States for sticky header visibility
  const [showPastTripsHeader, setShowPastTripsHeader] = useState(false);
  const [showUpcomingHeader, setShowUpcomingHeader] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const rafIdRef = useRef(null);

  // Timeline-based grouping states (iOS Photos style)  
  const [upcomingPlans, setUpcomingPlans] = useState([]);
  const [pastPlansByYear, setPastPlansByYear] = useState({});
  const [hasScrolledToUpcoming, setHasScrolledToUpcoming] = useState(false);

  // Add ref for initial load tracking
  const initialLoadRef = useRef(true);

  // Update local timeline state when hook data changes
  useEffect(() => {
    setUpcomingPlans(hookUpcomingPlans);
    setPastPlansByYear(hookPastPlansByYear);
  }, [hookUpcomingPlans, hookPastPlansByYear]);
  

  
  const handlePrivacyUpdate = async (planId, updates) => {
    try {
      await hookUpdatePlan(planId, updates);
      enqueueSnackbar('Travel plan shared successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error updating privacy:', error);
      enqueueSnackbar('Failed to share travel plan', { variant: 'error' });
      throw error; // Re-throw to prevent dialog from closing
    }
  };

  const formatDateRange = (startDate, endDate) => {
    return `${format(new Date(startDate), 'MMM d, yyyy')} - ${format(
      new Date(endDate),
      'MMM d, yyyy'
    )}`;
  };

  // Group events by day
  const groupEventsByDay = (events, startDate, dayCount) => {
    const planStartDate = startDate ? new Date(startDate) : null;
    if (planStartDate) {
      planStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
    }
    
    // Initialize grouped events object
    const groupedEvents = {};
    
    events.forEach(event => {
      let dayNumber;
      
      // If event has a day property, use it
      if (event.day) {
        dayNumber = event.day;
      } 
      // Otherwise calculate from dates if available
      else if (planStartDate && event.start) {
        const eventDate = new Date(event.start);
        eventDate.setHours(0, 0, 0, 0); // Normalize to start of day
        
        const dayDifference = differenceInDays(eventDate, planStartDate);
        dayNumber = dayDifference + 1; // Day 1, Day 2, etc.
      }
      // Default to day 1 if no other info available
      else {
        dayNumber = 1;
      }
      
      if (!groupedEvents[dayNumber]) {
        groupedEvents[dayNumber] = [];
      }
      
      groupedEvents[dayNumber].push(event);
    });
    
    // Sort events within each day by start time (if available)
    Object.keys(groupedEvents).forEach(day => {
      groupedEvents[day].sort((a, b) => {
        // If both events have start times, sort by time
        if (a.start && b.start) {
          return new Date(a.start) - new Date(b.start);
        }
        // If only one has a start time, put the one with a start time first
        if (a.start) return -1;
        if (b.start) return 1;
        // Otherwise just preserve the order they were added
        return 0;
      });
    });
    
    return groupedEvents;
  };
  
  const formatEventTime = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'h:mm a');
  };

  const getCategoryColor = (category) => {
    const colors = {
      flight: 'primary',
      hotel: 'secondary',
      activity: 'success',
      restaurant: 'warning',
      other: 'default',
    };
    return colors[category] || 'default';
  };
  
  const getPlanColorByDestination = (destination) => {
    if (!destination) return { border: 'default.main', background: 'grey.50' };
    
    // Simple hash function to get a consistent color by destination name
    const hash = destination.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    const colors = [
      { border: 'primary.main', background: 'primary.50' },
      { border: 'secondary.main', background: 'secondary.50' },
      { border: 'success.main', background: 'success.50' },
      { border: 'warning.main', background: 'warning.50' },
      { border: 'error.main', background: 'error.50' },
      { border: 'info.main', background: 'info.50' }
    ];
    
    return colors[hash % colors.length];
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async (formData) => {
    try {
      console.log('Creating travel plan with data:', formData);
      console.log('Current auth state:', { token: !!token, user: !!user });
      console.log('Token from localStorage:', !!localStorage.getItem('token'));
      
      const response = await createTravelPlan(formData);
      handleAddPlan(response.data); // Add to Redux store
      handleCloseDialog();
      enqueueSnackbar('Travel plan created successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error creating travel plan:', error);
      console.error('Error details:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data
      });
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to create travel plan', 
        { variant: 'error' }
      );
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Functions for Explore tab
  const searchPublicPlans = async (tags) => {
    setLoading(true);
    try {
      const params = {};
      
      // Handle tags parameter
      if (tags) {
        params.tags = Array.isArray(tags) ? tags.join(',') : tags;
      } else if (searchQuery && searchQuery.trim()) {
        // Smart search: treat the search query as both tags and destination
        params.tags = searchQuery.trim();
        params.destination = searchQuery.trim();
      }
      
      if (selectedCategories.length > 0) {
        params.categories = selectedCategories.join(',');
      }
      
      if (selectedDestinations.length > 0) {
        params.destinations = selectedDestinations.join(',');
      }
      
      console.log('Search params being sent:', params);
      
      // Fetch search results
      const searchResponse = await searchTravelPlans(params);
      
      // Update search results and liked plans
      let results = searchResponse.data;
      
      // If no results found and we had a specific search query, try searching all public plans
      if (results.length === 0 && (tags || searchQuery?.trim())) {
        console.log('No results found, fetching all public plans...');
        const allPublicResponse = await searchTravelPlans({});
        results = allPublicResponse.data;
      }
      
      // Create a Set of liked plan IDs from the isLiked flag
      const likedPlanIds = new Set(
        results
          .filter(plan => plan.isLiked)
          .map(plan => plan._id)
      );
      setLikedPlans(likedPlanIds);
      
      let filteredResults = results;
      
      if (dateRange[0] > 0 || dateRange[1] < 30) {
        const now = new Date();
        const minDate = new Date(now);
        minDate.setDate(now.getDate() + dateRange[0]);
        
        const maxDate = new Date(now);
        maxDate.setDate(now.getDate() + dateRange[1]);
        
        filteredResults = filteredResults.filter(plan => {
          const planStartDate = new Date(plan.startDate);
          return planStartDate >= minDate && planStartDate <= maxDate;
        });
      }
      
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching travel plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchPublicPlans();
  };

  const handleTagClick = (tag) => {
    handleSearchChange(tag);
    searchPublicPlans(tag);
  };

  const handleCategoryToggle = (category) => {
    const currentIndex = selectedCategories.indexOf(category);
    const newSelectedCategories = [...selectedCategories];
    
    if (currentIndex === -1) {
      newSelectedCategories.push(category);
    } else {
      newSelectedCategories.splice(currentIndex, 1);
    }
    
    setSelectedCategories(newSelectedCategories);
  };

  const handleDestinationToggle = (destination) => {
    const currentIndex = selectedDestinations.indexOf(destination);
    const newSelectedDestinations = [...selectedDestinations];
    
    if (currentIndex === -1) {
      newSelectedDestinations.push(destination);
    } else {
      newSelectedDestinations.splice(currentIndex, 1);
    }
    
    setSelectedDestinations(newSelectedDestinations);
  };

  const handleDateRangeChange = (event, newValue) => {
    setDateRange(newValue);
  };

  const handleApplyFilters = () => {
    setMobileFilterOpen(false);
    searchPublicPlans();
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setDateRange([0, 30]);
    setSelectedDestinations([]);
    searchPublicPlans();
  };

  const handleLike = async (planId) => {
    try {
      const response = await likeTravelPlan(planId);
      
      // Update the plan's like count in searchResults
      setSearchResults(prevResults => 
        prevResults.map(plan => 
          plan._id === planId 
            ? { ...plan, likes: { ...plan.likes, count: response.data.likes } }
            : plan
        )
      );
      
      // Add to liked plans set
      setLikedPlans(prev => new Set([...prev, planId]));
    } catch (error) {
      console.error('Error liking plan:', error);
    }
  };

  const handleUnlike = async (planId) => {
    try {
      const response = await unlikeTravelPlan(planId);
      
      // Update the plan's like count in searchResults
      setSearchResults(prevResults => 
        prevResults.map(plan => 
          plan._id === planId 
            ? { ...plan, likes: { ...plan.likes, count: response.data.likes } }
            : plan
        )
      );
      
      // Remove from liked plans set
      setLikedPlans(prev => {
        const newSet = new Set(prev);
        newSet.delete(planId);
        return newSet;
      });
    } catch (error) {
      console.error('Error unliking plan:', error);
    }
  };

  const handleCopyPlan = async (planId) => {
    try {
      const response = await copyTravelPlan(planId);
      
      enqueueSnackbar('Travel plan copied successfully!', { variant: 'success' });
      
      // Refresh the travel plans list to show the new copied plan
      handleRefresh();
      
      // Switch to "My Travel Plans" tab to show the copied plan
      setActiveTab(0);
    } catch (error) {
      console.error('Error copying travel plan:', error);
      enqueueSnackbar('Failed to copy travel plan', { variant: 'error' });
    }
  };

  // Filter panel content for Explore tab
  const filterContent = (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Filters
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CategoryIcon sx={{ mr: 1 }} />
          Trip Categories
        </Typography>
        <List dense>
          {categories.map((category) => (
            <ListItem key={category} dense button onClick={() => handleCategoryToggle(category)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selectedCategories.indexOf(category) !== -1}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={category} />
            </ListItem>
          ))}
        </List>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TodayIcon sx={{ mr: 1 }} />
          Date Range
        </Typography>
        <Box sx={{ px: 2 }}>
          <Slider
            value={dateRange}
            onChange={handleDateRangeChange}
            valueLabelDisplay="auto"
            min={0}
            max={30}
            marks={[
              { value: 0, label: 'Now' },
              { value: 30, label: '30 days' },
            ]}
          />
        </Box>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PublicIcon sx={{ mr: 1 }} />
          Popular Destinations
        </Typography>
        <List dense>
          {popularDestinations.map((destination) => (
            <ListItem key={destination} dense button onClick={() => handleDestinationToggle(destination)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={selectedDestinations.indexOf(destination) !== -1}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={destination} />
            </ListItem>
          ))}
        </List>
      </Box>
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={handleClearFilters}>
          Clear All
        </Button>
        <Button variant="contained" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
      </Box>
    </Box>
  );

  // Add formatDate function
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Add useEffect to fetch liked plans on component mount
  useEffect(() => {
    const fetchLikedPlans = async () => {
      try {
        const response = await getLikedTravelPlans();
        // Convert array of liked plan IDs to a Set
        const likedPlanIds = new Set(response.data.map(plan => plan._id));
        setLikedPlans(likedPlanIds);
      } catch (error) {
        console.error('Error fetching liked plans:', error);
      }
    };

    fetchLikedPlans();
  }, [token]);

  // Load initial public plans when Explore tab is accessed
  useEffect(() => {
    if (activeTab === 1) {
      // Load some public plans initially
      searchPublicPlans();
    }
  }, [activeTab]);

  // Add a function to handle accordion expansion
  const handleAccordionChange = (planId, day) => (event, isExpanded) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [`${planId}-day-${day}`]: isExpanded
    }));
  };
  
  // Handler for card click to open trip details
  const handleCardClick = (plan) => {
    setSelectedTrip(plan);
    setTripDetailsOpen(true);
  };
  
  // Handler to close trip details
  const handleCloseTripDetails = async () => {
    setTripDetailsOpen(false);
    
    // Refetch the travel plan data to get updated events
    if (selectedTrip?._id || selectedTrip?.id) {
      try {
        const { getTravelPlan } = await import('../services/api');
        const response = await getTravelPlan(selectedTrip._id || selectedTrip.id);
        const updatedPlan = response.data;
        
        // Update the selected trip with fresh data
        setSelectedTrip(updatedPlan);
        
        // Also refresh the full travel plans list to ensure consistency
        handleRefresh();
      } catch (error) {
        console.error('Error refetching travel plan:', error);
      }
    }
    
    setSelectedTrip(null);
  };

  // Handler for trip updates from TripDetails component
  const handleTripUpdate = (updatedTripData) => {
    setSelectedTrip(updatedTripData);
  };

  // Auto-scroll to upcoming section on page load (seamless timeline)
  useEffect(() => {
    // Only run on initial load, not on subsequent data changes
    if (activeTab === 0 && !hasScrolledToUpcoming && Object.keys(pastPlansByYear).length > 0 && initialLoadRef.current) {
      setTimeout(() => {
        const upcomingSection = document.getElementById('upcoming-section');
        if (upcomingSection) {
          upcomingSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
          setHasScrolledToUpcoming(true);
          initialLoadRef.current = false; // Mark initial load as complete
        }
      }, 800); // Slightly longer delay to ensure smooth loading
    }
  }, [activeTab, pastPlansByYear, hasScrolledToUpcoming]);

  // Scroll detection for sticky section headers with debouncing and buffering
  useEffect(() => {
    const handleScroll = () => {
      if (activeTab !== 0) return; // Only on My Travel tab
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce scroll updates to prevent rapid changes
      scrollTimeoutRef.current = setTimeout(() => {
        const pastSection = document.getElementById('past-travels-section');
        const upcomingSection = document.getElementById('upcoming-section');
        
        // If sections exist, use scroll-based detection
        if (pastSection && upcomingSection) {
          const scrollY = window.scrollY;
          const pastSectionTop = pastSection.offsetTop - 150; // Account for main header + tabs
          const upcomingSectionTop = upcomingSection.offsetTop - 150;
          
          // Show Past Trips header when scrolling through past travels
          if (scrollY >= pastSectionTop && scrollY < upcomingSectionTop) {
            setShowPastTripsHeader(true);
            setShowUpcomingHeader(false);
            
            // Detect which year section is currently visible
            const years = Object.keys(pastPlansByYear).sort((a, b) => parseInt(b) - parseInt(a));
            let bestMatch = null;
            let bestOverlap = 0;
            
            // Find the year section with the best overlap
            for (const year of years) {
              const yearElement = document.getElementById(`year-section-${year}`);
              if (yearElement) {
                const yearTop = yearElement.offsetTop - 250; // Account for headers
                const yearBottom = yearTop + yearElement.offsetHeight;
                
                // Calculate overlap with current viewport
                const viewportTop = scrollY;
                const viewportBottom = scrollY + window.innerHeight;
                
                const overlapTop = Math.max(yearTop, viewportTop);
                const overlapBottom = Math.min(yearBottom, viewportBottom);
                const overlap = Math.max(0, overlapBottom - overlapTop);
                
                // Use the year section with the most overlap
                if (overlap > bestOverlap && overlap > 50) { // Lower threshold for better detection
                  bestOverlap = overlap;
                  bestMatch = year;
                }
              }
            }
            
            // Always set a year if we're in past section, prefer bestMatch or fallback to first year
            if (bestMatch) {
              setCurrentPastTripsYear(bestMatch);
            } else if (years.length > 0) {
              setCurrentPastTripsYear(years[0]); // Fallback to most recent year
            }
          }
          // Show Upcoming header when scrolling through upcoming section
          else if (scrollY >= upcomingSectionTop) {
            setShowPastTripsHeader(false);
            setShowUpcomingHeader(true);
            setCurrentPastTripsYear(null);
          }
          // Hide both when at the very top (before past section)
          else {
            setShowPastTripsHeader(false);
            setShowUpcomingHeader(false);
            setCurrentPastTripsYear(null);
          }
        }
        // Fallback: If we can't find sections but have past travels data, show past trips header
        else if (Object.keys(pastPlansByYear).length > 0) {
          setShowPastTripsHeader(true);
          setShowUpcomingHeader(false);
          const years = Object.keys(pastPlansByYear).sort((a, b) => parseInt(b) - parseInt(a));
          if (years.length > 0) {
            setCurrentPastTripsYear(years[0]); // Show most recent year
          }
        }
        // Fallback: If we have upcoming plans but no sections detected
        else if (upcomingPlans.length > 0) {
          setShowPastTripsHeader(false);
          setShowUpcomingHeader(true);
          setCurrentPastTripsYear(null);
        }
              }, 50); // Slower debouncing for maximum stability
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [activeTab, pastPlansByYear, upcomingPlans]);

  // Reset scroll state when switching tabs or applying filters
  useEffect(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    
    // Reset initial load tracking when switching away from My Travel tab
    if (activeTab !== 0) {
      initialLoadRef.current = true;
      setHasScrolledToUpcoming(false);
    }
    
    setShowPastTripsHeader(false);
    setShowUpcomingHeader(false);
    setCurrentPastTripsYear(null);
  }, [activeTab, searchQuery, tagFilter, destinationFilter]);

  // Initialize headers when data is loaded
  useEffect(() => {
    if (activeTab === 0 && Object.keys(pastPlansByYear).length > 0) {
      // Show past trips header by default when past travels exist
      const years = Object.keys(pastPlansByYear).sort((a, b) => parseInt(b) - parseInt(a));
      setShowPastTripsHeader(true);
      setShowUpcomingHeader(false);
      if (years.length > 0) {
        setCurrentPastTripsYear(years[0]); // Show most recent year initially
      }
    } else if (activeTab === 0 && upcomingPlans.length > 0 && Object.keys(pastPlansByYear).length === 0) {
      // Show upcoming header if only upcoming plans exist
      setShowPastTripsHeader(false);
      setShowUpcomingHeader(true);
      setCurrentPastTripsYear(null);
    }
  }, [activeTab, pastPlansByYear, upcomingPlans]);

  return (
    <Box sx={{ 
      backgroundColor: '#FFF5E6', 
      minHeight: '100vh',
      // Add pulse animation for scroll indicator
      '@keyframes pulse': {
        '0%': { opacity: 0.6 },
        '50%': { opacity: 1 },
        '100%': { opacity: 0.6 }
      }
    }}>
      <Helmet>
        <title>My Dashboard - Pelican Beaks</title>
      </Helmet>
      
      {/* Sticky Header Container */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        mx: -3, // Negative margin to extend beyond parent padding
        width: 'calc(100% + 48px)', // Compensate for negative margins
        borderTop: 'none', // Remove any top border
      }}>
        {/* New Dashboard Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 50%, #FFA07A 100%)',
          padding: { 
            xs: '20px 24px', // Mobile: smaller padding
            sm: '20px 48px'  // Desktop: maintain content alignment
          },
          position: 'relative',
          overflow: 'hidden',
          borderTop: 'none', // Remove any top border
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
          }
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1
          }}>
            <Box 
              onClick={() => navigate('/')}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.9
                }
              }}
            >
              {/* Menu button for mobile */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (handleDrawerToggle) handleDrawerToggle();
                }}
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  width: 40,
                  height: 40,
                }}
              >
                <MenuIcon />
              </IconButton>
              
              <Box sx={{
                width: 48,
                height: 48,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                <Avatar 
                  src={process.env.PUBLIC_URL + '/PelicanLogo.ico'} 
                  alt="Pelican Beaks Logo" 
                  sx={{ width: 36, height: 36 }} 
                />
              </Box>
              <Typography variant="h4" sx={{
                color: 'white',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                fontSize: { xs: '20px', sm: '24px' }
              }}>
                Pelican Beaks
              </Typography>
            </Box>
            <IconButton
              onClick={() => navigate('/dashboard/profile')}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: 'white',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  backgroundColor: 'white',
                  transform: 'scale(1.05)',
                }
              }}
            >
              {user?.profilePicture ? (
                <Avatar 
                  src={user.profilePicture} 
                  alt={user.name}
                  sx={{ width: 24, height: 24 }}
                />
              ) : (
                <PersonIcon sx={{ color: '#FF7B6B' }} />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Tab Navigation with new styling */}
        <Paper sx={{ 
          backgroundColor: 'white',
          padding: { 
            xs: '0 24px', // Mobile
            sm: '0 48px'  // Desktop: match the header padding
          },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid rgba(255, 122, 107, 0.1)',
          borderRadius: 0,
          boxShadow: 'none'
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#FF7B6B',
                height: 3
              }
            }}
          >
            <Tab
              label="MY TRAVEL"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                letterSpacing: '0.5px',
                color: '#999',
                '&.Mui-selected': {
                  color: '#FF7B6B'
                }
              }}
            />
            <Tab 
              label="EXPLORE" 
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                letterSpacing: '0.5px',
                color: '#999',
                '&.Mui-selected': {
                  color: '#FF7B6B'
                }
              }}
            />
          </Tabs>
          
          {activeTab === 0 && (
            <AnimatedTravelButton
              onClick={() => setOpenDialog(true)}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                backgroundColor: 'white',
                color: '#FF7B6B',
                border: '2px solid #FF7B6B',
                '& .MuiButton-startIcon': {
                  color: '#FF7B6B'
                },
                '&:hover': {
                  backgroundColor: '#FFF5F0',
                  color: '#FF5B4B',
                  border: '2px solid #FF5B4B',
                  '& .MuiButton-startIcon': {
                    color: '#FF5B4B'
                  }
                }
              }}
            >
              New Travel Plan
            </AnimatedTravelButton>
          )}
        </Paper>
      </Box>

      {/* Sticky Section Headers */}
      {showPastTripsHeader && (
        <Paper sx={{
          position: 'sticky',
          top: '137px', // Combined height of header + tab bar
          zIndex: 40,
          py: 2,
          px: { xs: 3, sm: 6 }, // Responsive padding
          mx: -3, // Negative margin to extend full width
          width: 'calc(100% + 48px)', // Compensate for negative margins
          backgroundColor: '#FFF5E6',
          backdropFilter: 'blur(10px)',
          borderRadius: 0,
          borderBottom: '1px solid',
          borderBottomColor: 'rgba(255, 122, 107, 0.1)',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'translateZ(0)', // Enable hardware acceleration
          willChange: 'transform, opacity' // Optimize for animations
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              color: '#4A5568',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              '&::before': {
                content: '""',
                width: '4px',
                height: '24px',
                background: 'linear-gradient(180deg, #FF9A8B 0%, #FF7B6B 100%)',
                borderRadius: '2px'
              }
            }}
          >
            {currentPastTripsYear ? `Past Trips in ${currentPastTripsYear}` : 'Past Trips'}
          </Typography>
        </Paper>
      )}

      {showUpcomingHeader && (
        <Paper sx={{
          position: 'sticky',
          top: '137px', // Combined height of header + tab bar
          zIndex: 40,
          py: 2,
          px: { xs: 3, sm: 6 }, // Responsive padding
          mx: -3, // Negative margin to extend full width
          width: 'calc(100% + 48px)', // Compensate for negative margins
          backgroundColor: '#FFF5E6',
          backdropFilter: 'blur(10px)',
          borderRadius: 0,
          borderBottom: '1px solid',
          borderBottomColor: 'rgba(255, 122, 107, 0.1)',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'translateZ(0)', // Enable hardware acceleration
          willChange: 'transform, opacity' // Optimize for animations
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              color: '#4A5568',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              '&::before': {
                content: '""',
                width: '4px',
                height: '24px',
                background: 'linear-gradient(180deg, #FF9A8B 0%, #FF7B6B 100%)',
                borderRadius: '2px'
              }
            }}
          >
            Upcoming Trips
          </Typography>
        </Paper>
      )}

      {/* Main Content */}
      <Box sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, 
        pt: 4,
        maxWidth: '1200px',
        margin: '0 auto'
      }}>

        {activeTab === 0 ? (
          <Box>
{/* Timeline View (no filters) */}
            {(!searchQuery && tagFilter.length === 0 && !destinationFilter) ? (
              travelPlans.length === 0 ? (
                <Paper sx={{ 
                  p: 6, 
                  textAlign: 'center',
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                }}>
                  <Box sx={{
                    width: 120,
                    height: 120,
                    background: 'linear-gradient(135deg, #FFE5D4 0%, #FFB5A7 100%)',
                    borderRadius: '50%',
                    margin: '0 auto 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <TravelExploreIcon sx={{ fontSize: 60, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#4A5568', mb: 1 }}>
                    No travel plans found
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Get started by creating your first travel plan!
                  </Typography>
                  <AnimatedTravelButton
                    onClick={() => setOpenDialog(true)}
                    sx={{ 
                      background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #FF8A7B 0%, #FF6B5B 100%)',
                      }
                    }}
                  >
                    Create Travel Plan
                  </AnimatedTravelButton>
                </Paper>
              ) : (
                              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 6
              }}>
                {/* Past Travel Plans Section - iOS Photos Style */}
                {Object.keys(pastPlansByYear).length > 0 && (
                  <Box id="past-travels-section" sx={{ mb: 8 }}>
                    {Object.keys(pastPlansByYear)
                      .sort((a, b) => parseInt(b) - parseInt(a)) // Most recent years first
                      .map((year) => (
                        <Box key={year} id={`year-section-${year}`} sx={{ mb: 8 }}>
                          {/* Year Header - iOS Photos Style */}
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 700,
                              color: '#4A5568',
                              mb: 4,
                              fontSize: { xs: '1.8rem', md: '2.2rem' },
                              letterSpacing: '0.5px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              '&::before': {
                                content: '""',
                                width: '4px',
                                height: '28px',
                                background: 'linear-gradient(180deg, #FF9A8B 0%, #FF7B6B 100%)',
                                borderRadius: '2px'
                              }
                            }}
                          >
                            {year}
                          </Typography>
                          
                          {/* Plans for this year */}
                          <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: 3
                          }}>
                            {pastPlansByYear[year].map((plan, idx) => (
                              <Grow
                                in={true}
                                timeout={1000}
                                style={{ 
                                  transformOrigin: '0 0 0', 
                                  transitionDelay: `${idx * 50}ms` 
                                }}
                                key={plan._id}
                              >
                                <Box>
                                  <TravelPlanCard
                                    plan={plan}
                                    isOwner={true}
                                    expandedAccordions={expandedAccordions}
                                    onAccordionChange={handleAccordionChange}
                                    onDelete={hookDeletePlan}
                                    onEdit={(id) => navigate(`/travel-plans/${id}/edit`)}
                                    onEditCoverImage={handleEditCoverImage}
                                    onPrivacyUpdate={handlePrivacyUpdate}
                                    activeItemIndex={plan.events?.length ? Math.floor(plan.events.length / 2) : 0}
                                    onItemSelected={(item) => console.log('Selected item:', item)}
                                    onCardClick={handleCardClick}
                                  />
                                </Box>
                              </Grow>
                            ))}
                          </Box>
                        </Box>
                      ))}

                    {/* Transition divider between past and upcoming */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      my: 6,
                      opacity: 0.5 
                    }}>
                      <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mx: 2, 
                          color: 'text.secondary',
                          fontWeight: 'medium'
                        }}
                      >
                        Present Day
                      </Typography>
                      <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                    </Box>
                  </Box>
                )}

                {/* Upcoming Section */}
                <Box 
                  id="upcoming-section" 
                  sx={{ 
                    minHeight: '100vh', 
                    pt: Object.keys(pastPlansByYear).length > 0 ? 2 : 0,
                    position: 'relative'
                  }}
                >
                  {/* Upcoming Section Title - Regular (non-sticky) */}
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#4A5568',
                      mb: 4,
                      fontSize: { xs: '1.8rem', md: '2.2rem' },
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&::before': {
                        content: '""',
                        width: '4px',
                        height: '28px',
                        background: 'linear-gradient(180deg, #FF9A8B 0%, #FF7B6B 100%)',
                        borderRadius: '2px'
                      }
                    }}
                  >
                    Upcoming Trips
                  </Typography>

                  {/* Empty state for upcoming when no upcoming plans */}
                  {upcomingPlans.length === 0 ? (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 8,
                      backgroundColor: 'white',
                      borderRadius: '20px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                    }}>
                      <Box sx={{
                        width: 120,
                        height: 120,
                        background: 'linear-gradient(135deg, #FFE5D4 0%, #FFB5A7 100%)',
                        borderRadius: '50%',
                        margin: '0 auto 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <TravelExploreIcon sx={{ fontSize: 60, color: 'white' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#4A5568', mb: 1 }}>
                        No upcoming travel plans
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Ready to plan your next adventure?
                      </Typography>
                      <AnimatedTravelButton
                        onClick={() => setOpenDialog(true)}
                        sx={{ 
                          background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #FF8A7B 0%, #FF6B5B 100%)',
                          }
                        }}
                      >
                        Create Travel Plan
                      </AnimatedTravelButton>
                    </Box>
                  ) : (
                    <>                      
                      <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: 3
                      }}>
                        {upcomingPlans.map((plan, idx) => (
                          <Grow
                            in={true}
                            timeout={1000}
                            style={{ transformOrigin: '0 0 0', transitionDelay: `${idx * 100}ms` }}
                            key={plan._id}
                          >
                            <Box>
                              <TravelPlanCard
                                plan={plan}
                                isOwner={true}
                                expandedAccordions={expandedAccordions}
                                onAccordionChange={handleAccordionChange}
                                onDelete={hookDeletePlan}
                                onEdit={(id) => navigate(`/travel-plans/${id}/edit`)}
                                onEditCoverImage={handleEditCoverImage}
                                onPrivacyUpdate={handlePrivacyUpdate}
                                activeItemIndex={plan.events?.length ? Math.floor(plan.events.length / 2) : 0}
                                onItemSelected={(item) => console.log('Selected item:', item)}
                                onCardClick={handleCardClick}
                              />
                            </Box>
                          </Grow>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
              )
            ) : (
              /* Filtered View */
              filteredPlans.length === 0 ? (
                <Paper sx={{ 
                  p: 6, 
                  textAlign: 'center',
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                }}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No travel plans found
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    Try adjusting your filters or create your first travel plan.
                  </Typography>
                  <AnimatedTravelButton
                    onClick={() => setOpenDialog(true)}
                    sx={{ 
                      mt: 2,
                      background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #FF8A7B 0%, #FF6B5B 100%)',
                      }
                    }}
                  >
                    Create Travel Plan
                  </AnimatedTravelButton>
                </Paper>
              ) : (
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: 3
                }}>
                  {filteredPlans.map((plan, idx) => (
                    <Grow
                      in={true}
                      timeout={1000}
                      style={{ transformOrigin: '0 0 0', transitionDelay: `${idx * 100}ms` }}
                      key={plan._id}
                    >
                      <Box>
                        <TravelPlanCard
                          plan={plan}
                          isOwner={true}
                          expandedAccordions={expandedAccordions}
                          onAccordionChange={handleAccordionChange}
                          onDelete={hookDeletePlan}
                          onEdit={(id) => navigate(`/travel-plans/${id}/edit`)}
                          onEditCoverImage={handleEditCoverImage}
                          onPrivacyUpdate={handlePrivacyUpdate}
                          activeItemIndex={plan.events?.length ? Math.floor(plan.events.length / 2) : 0}
                          onItemSelected={(item) => console.log('Selected item:', item)}
                          onCardClick={handleCardClick}
                        />
                      </Box>
                    </Grow>
                  ))}
                </Box>
              )
            )}
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSearch} sx={{ mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={10} sm={11} md={10}>
                  <TextField 
                    fullWidth
                    label="Search by destination, activity or tag"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={2} sm={1} md={2} sx={{ display: 'flex' }}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    sx={{ flexGrow: { xs: 1, md: 0 }, height: '100%' }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Search'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel id="explore-destination-label">Filter by Destination</InputLabel>
                  <Select
                    labelId="explore-destination-label"
                    value={destinationFilter}
                    onChange={handleDestinationFilterChange}
                    label="Filter by Destination"
                  >
                    <MenuItem value="">All Destinations</MenuItem>
                    {availableDestinations.map((destination) => (
                      <MenuItem key={destination} value={destination}>
                        {destination}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Autocomplete
                  multiple
                  options={availableTags}
                  value={tagFilter}
                  onChange={handleTagFilterChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Tags"
                      placeholder="Select tags"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <FilterIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={handleClearFilters}
                  sx={{ height: '100%' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Popular Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {popularTags.map((tag) => (
                  <Chip 
                    key={tag} 
                    label={tag} 
                    clickable 
                    onClick={() => handleTagClick(tag)} 
                    color={searchQuery === tag ? "primary" : "default"}
                  />
                ))}
              </Box>
            </Box>
            
            <Divider sx={{ mb: 4 }} />
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : searchResults.length === 0 ? (
              <Box sx={{ textAlign: 'center', my: 4 }}>
                <TravelExploreIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No travel plans found
                </Typography>
                <Typography color="text.secondary">
                  Try searching with different tags or browse popular destinations
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                {searchResults.map((plan, idx) => (
                  <Grow
                    in={true}
                    timeout={1000}
                    style={{ transformOrigin: '0 0 0', transitionDelay: `${idx * 100}ms` }}
                    key={plan._id}
                  >
                    <Box sx={{ width: '100%' }}>
                      <TravelPlanCard
                        plan={plan}
                        isExploreTab={true}
                        isLiked={likedPlans.has(plan._id)}
                        onLike={handleLike}
                        onUnlike={handleUnlike}
                        onCopy={handleCopyPlan}
                        expandedAccordions={expandedAccordions}
                        onAccordionChange={handleAccordionChange}
                        activeItemIndex={plan.events?.length ? Math.floor(plan.events.length / 2) : 0}
                        onItemSelected={(item) => console.log('Selected item:', item)}
                        onCardClick={handleCardClick}
                      />
                    </Box>
                  </Grow>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Floating Action Button - only show on mobile when button in header is hidden */}
      {activeTab === 0 && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 56,
            height: 56,
            display: { xs: 'flex', sm: 'none' }, // Only show on mobile
            background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 100%)',
            boxShadow: '0 6px 20px rgba(255, 122, 107, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #FF8A7B 0%, #FF6B5B 100%)',
              transform: 'scale(1.1)',
              boxShadow: '0 8px 24px rgba(255, 122, 107, 0.5)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            margin: 0,
            maxWidth: 'none',
            maxHeight: 'none',
            width: '100vw',
            height: '100vh',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: 0,
            overflow: 'hidden',
            position: 'relative'
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <TravelPlanForm
          onCancel={handleCloseDialog}
          onSubmit={handleSubmit}
        />
      </Dialog>

      {/* Trip Details Modal */}
      <TripDetails
        open={tripDetailsOpen}
        onClose={handleCloseTripDetails}
        tripData={selectedTrip}
        onTripUpdate={handleTripUpdate}
      />

      {/* Cover Image Upload Dialog */}
      <CoverImageUploadDialog
        open={coverImageDialogOpen}
        onClose={handleCloseCoverImageDialog}
        onUpload={handleCoverImageUpload}
        currentImage={selectedPlanForCover?.coverImageUrl}
        planTitle={selectedPlanForCover?.destination || selectedPlanForCover?.title}
      />
    </Box>
  );
}

export default Dashboard; 